import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { Modal } from 'antd';
import { useMemo, useState } from 'react';

import type { IComponentSchema } from '../../types';
import SimpleTriggerActionButton, {
  type ISimpleTriggerActionButtonProps,
} from '../simple-trigger-action-button';
import ActionForm from '../trigger-action-button/ActionForm';
import { removeInternalValues } from '../form-trigger-action-button/utils';
import { usePage } from '../../context/PageContext';
import { fetchModelInstanceApi } from '../../hooks/api/useFetchModelInstance';
import { enrichSparseIdRefs } from '../../utils/enrichInternalReferenceData';
import { importInstanceIntoForm } from '../../utils/importInstanceData';
import { parseSubSchema } from '../form-recursion-item/utils';
import { useMessage, useToggleVisibility } from '../../../hooks';
import { searchJmesPath } from '../../../utils';

export interface IFormImportInstanceProps
  extends ISimpleTriggerActionButtonProps {
  transformer?: string;
  keepFields?: string[];
  successMessage?: string;
  actionForm?: {
    title?: string;
    modalWidth?: number;
    cancelText?: string;
    confirmText?: string;
  };
  childSchema?: IComponentSchema<any>;
}

const FormImportInstance = observer(
  ({ schema }: { schema: IComponentSchema<IFormImportInstanceProps> }) => {
    const field = useField<Field>();
    const pageData = usePage();
    const message = useMessage();
    const { isVisible, show, hide } = useToggleVisibility();
    const [isImporting, setIsImporting] = useState(false);

    const {
      transformer,
      keepFields,
      successMessage,
      actionForm,
      childSchema,
      ...buttonProps
    } = {
      ...(schema?.['x-component-props'] || {}),
      ...(field?.componentProps || {}),
    } as IFormImportInstanceProps;

    // The parent Form escapes nested child forms to strings; parse it back.
    const actionFormSchema = useMemo(
      () => parseSubSchema(childSchema),
      [childSchema]
    );

    const handleImport = async (values: Record<string, unknown>) => {
      const sourceId = searchJmesPath(
        { form: removeInternalValues(values) },
        transformer || ''
      );
      if (!sourceId) {
        message.error('Select an instance to import from.');
        return;
      }

      setIsImporting(true);
      const source = await fetchModelInstanceApi(sourceId).finally(() =>
        setIsImporting(false)
      );
      importInstanceIntoForm(
        field.form,
        pageData.context['@'] || {},
        enrichSparseIdRefs(source.data || {}),
        keepFields || []
      );
      hide();
      message.success(successMessage || 'Data imported. Review and save.');
    };

    if (buttonProps.visible === false) return null;

    return (
      <>
        <SimpleTriggerActionButton
          {...buttonProps}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            show();
          }}
        />
        {isVisible && (
          <div onClick={e => e.stopPropagation()}>
            <Modal
              open={isVisible}
              onCancel={hide}
              footer={null}
              title={actionForm?.title || buttonProps.text}
              destroyOnHidden
              maskClosable={false}
              width={actionForm?.modalWidth || 720}
            >
              <ActionForm
                schema={actionFormSchema}
                onSubmit={handleImport}
                onCancel={hide}
                cancelButtonText={actionForm?.cancelText || 'Cancel'}
                saveButtonText={actionForm?.confirmText || 'Import'}
                parentContext={{}}
                isSubmitting={isImporting}
              />
            </Modal>
          </div>
        )}
      </>
    );
  }
);

export default FormImportInstance;
